import { useEffect, useState } from "react";
import { IoClose } from "react-icons/io5";
import { MdVerified } from "react-icons/md";
import { FaPlus } from "react-icons/fa";
import { TiTick } from "react-icons/ti";
import { ImSpinner2 } from "react-icons/im";
import useDisableBackgroundScroll from "../../hooks/useDisableBackgroundScroll";
import { FollowUser, GetProfileFollowingList, UnfollowUser } from "../../services/api";
import { getMediaUrl } from "../../utils/helpers";

export default function MyFollowing({ onClose }) {
    useDisableBackgroundScroll(true);

    const [followingList, setFollowingList] = useState([]);
    const [followings, setFollowings] = useState([]);
    const [loadingUsers, setLoadingUsers] = useState(new Set());

    useEffect(() => {
        if (followingList?.length) {
            const formatted = followingList.map(item => ({
                ...item,
                is_following: true, // default
            }));
            setFollowings(formatted);
        }
    }, [followingList]);

    useEffect(() => {
        fetchFollowingList();
    }, []);

    const fetchFollowingList = () => {
        GetProfileFollowingList()
            .then((res) => {
                setFollowingList(res.data);
            })
            .catch((err) => {
                console.log(err)
            });
    }

    const OnFollowClick = async (userId) => {

        // prevent double click on same user
        if (loadingUsers.has(userId)) return;

        // mark this user as loading
        setLoadingUsers(prev => new Set(prev).add(userId));

        // find current follow state
        const target = followings.find(
            item => item.followingId._id === userId
        );

        if (!target) {
            setLoadingUsers(prev => {
                const next = new Set(prev);
                next.delete(userId);
                return next;
            });
            return;
        }

        const isCurrentlyFollowing = target.is_following;

        // 1️⃣ Optimistic UI update
        setFollowings(prev =>
            prev.map(item =>
                item.followingId._id === userId
                    ? { ...item, is_following: !isCurrentlyFollowing }
                    : item
            )
        );

        try {
            // 2️⃣ Call correct API
            if (isCurrentlyFollowing) {
                await UnfollowUser(userId);
            } else {
                await FollowUser(userId);
            }
        } catch (err) {
            console.error(err);

            // 3️⃣ Rollback on failure
            setFollowings(prev =>
                prev.map(item =>
                    item.followingId._id === userId
                        ? { ...item, is_following: isCurrentlyFollowing }
                        : item
                )
            );
        } finally {
            // 4️⃣ remove loading for this user
            setLoadingUsers(prev => {
                const next = new Set(prev);
                next.delete(userId);
                return next;
            });
        }
    };

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">

            {/* ❌ NO overflow here */}
            <div className="bg-[#0B0B0B] rounded-2xl w-[90%] max-w-[480px] h-[80vh] flex flex-col text-white">

                {/* 🔒 FIXED HEADER */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-800 shrink-0">
                    <h2 className="text-xl font-bold">Followings</h2>
                    <button onClick={onClose} className="text-2xl">
                        <IoClose />
                    </button>
                </div>

                {/* ✅ ONLY SCROLL AREA */}
                <div className="flex-1 overflow-y-auto no-scrollbar px-4">

                    {followings.length === 0 && (
                        <div className="flex justify-center items-center h-full">
                            <p className="text-gray-400 text-lg text-center">
                                Start following people you like
                            </p>
                        </div>
                    )}

                    {followings.map((item, _id) => (
                        <div
                            key={_id}
                            className="flex items-center justify-between py-4"
                        >
                            {/* LEFT */}
                            <div className="flex items-center gap-3">
                                <img
                                    src={getMediaUrl(item.followingId.ppImageURL)}
                                    alt="profile"
                                    className="w-12 h-12 rounded-full object-cover"
                                />
                                <div className="flex items-center gap-1 font-medium">
                                    {item.followingId.name}
                                    {item.followingId.is_verified && (
                                        <MdVerified className="text-blue-400 text-lg" />
                                    )}
                                </div>
                            </div>

                            {/* RIGHT */}
                            <button
                                onClick={() => OnFollowClick(item.followingId._id)}
                                disabled={loadingUsers.has(item.followingId._id)}
                                className={`flex items-center justify-center px-4 py-1.5 rounded-full text-sm transition
                                        min-w-[110px]
                                        ${item.is_following
                                        ? "bg-red-500 text-white"
                                        : "border border-gray-500 text-white hover:bg-white/10"
                                    }
                                        ${loadingUsers.has(item.followingId._id) && "cursor-not-allowed opacity-70"}
                                    `}
                            >
                                {loadingUsers.has(item.followingId._id) ? (
                                    <ImSpinner2 className="animate-spin text-base" />
                                ) : item.is_following ? (
                                    <div className="flex items-center gap-2">
                                        <TiTick className="text-sm" />
                                        <span>Followed</span>
                                    </div>
                                ) : (
                                    <div className="flex items-center gap-2">
                                        <FaPlus className="text-sm" />
                                        <span>Follow</span>
                                    </div>
                                )}
                            </button>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}